import { IconCheck, IconClock } from "@tabler/icons-react";
import cn from "classnames";
import ExternalPrintFormDownloadBtn from "components/shared/externalPrintFormDownloadBtn";
import ShippingOrderInfo, {
    ShippingOrderDeclineBadge,
    ShippingOrderStatusBadge,
} from "components/shared/shippingOrderInfo";
import { shippingOrderTitleTypesEnum } from "helpers/enums";
import { canDownloadExternalPrintForms } from "helpers/printForms";
import { getShippingOrderCargoTotalWeightKg, isShippingOrderResponseTitleSigned } from "helpers/shippingOrder";
import { formatNumber } from "helpers/system";
import { useExternalPrintFormDownload } from "hooks/useExternalPrintFormDownload";
import React, { useState } from "react";
import { getPrintFormFromExternal } from "sources/shippingOrders";

const basePillClass = "badge b-etrn__pill mr-1 border";

export const ShippingOrderSection = ({ shippingOrder, renderActions, defaultCollapsed = true, roles = null }) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    const { download, isDownloading } = useExternalPrintFormDownload({
        documentType: "shippingOrder",
        requestFn: getPrintFormFromExternal,
    });

    if (!shippingOrder) {
        return null;
    }

    const renderSummaryCard = () => {
        const details = shippingOrder.title1Content || shippingOrder.details || {};
        const titles = shippingOrder.titles || [];
        const orderNum = details.orderNumber || shippingOrder.id;
        const hasT1 = titles.some((t) => t.titleType === shippingOrderTitleTypesEnum.T1);

        // Address route preview
        const points = details.addressPoints || [];
        const routeText =
            points.length > 0
                ? points
                      .map((p) => p.address)
                      .filter(Boolean)
                      .slice(0, 2)
                      .join(" → ")
                : null;

        // Cargo info preview
        const cargos = details.cargos || [];
        const cargoWeight = getShippingOrderCargoTotalWeightKg(cargos);
        const cargoVolume = cargos.reduce((acc, c) => acc + (Number(c.volume ?? c.volumeCubicMeters) || 0), 0);
        const cargoTextParts = [];
        if (cargoWeight > 0) {
            cargoTextParts.push(`${formatNumber(cargoWeight)} кг`);
        }
        if (cargoVolume > 0) {
            cargoTextParts.push(`${cargoVolume} м³`);
        }
        const cargoText = cargoTextParts.length > 0 ? cargoTextParts.join(", ") : null;

        return (
            <div className="b-etrn">
                <div className="b-etrn__card mb-2">
                    <div className="d-flex flex-wrap align-items-center justify-content-between">
                        <div className="d-flex align-items-center flex-wrap">
                            <span className="font-weight-bold mr-3">ЭЗЗ №{orderNum}</span>
                            <div className="d-flex align-items-center mr-3">
                                <ShippingOrderStatusBadge status={shippingOrder.status} />
                                <ShippingOrderDeclineBadge shippingOrder={shippingOrder} />
                            </div>
                            <div className="d-flex align-items-center">
                                {hasT1 ? (
                                    <span
                                        className={`${basePillClass} badge-success border-success`}
                                        title="Информация фрахтователя (Т1) получена"
                                    >
                                        <IconCheck size={11} className="mr-1" />
                                        Т1
                                    </span>
                                ) : (
                                    <span
                                        className={`${basePillClass} badge-light text-muted border-secondary`}
                                        title="Информация фрахтователя (Т1) отсутствует"
                                    >
                                        Т1
                                    </span>
                                )}
                                {isShippingOrderResponseTitleSigned(shippingOrder) ? (
                                    <span
                                        className={`${basePillClass} badge-success border-success`}
                                        title="Информация фрахтовщика (Т2) подписана"
                                    >
                                        <IconCheck size={11} className="mr-1" />
                                        Т2
                                    </span>
                                ) : hasT1 ? (
                                    <span
                                        className={`${basePillClass} badge-warning border-warning`}
                                        title="Информация фрахтовщика (Т2) ожидает подписания"
                                    >
                                        <IconClock size={11} className="mr-1" />
                                        Т2
                                    </span>
                                ) : (
                                    <span
                                        className={`${basePillClass} badge-light text-muted border-secondary`}
                                        title="Информация фрахтовщика (Т2) не сформирована"
                                    >
                                        Т2
                                    </span>
                                )}
                            </div>
                        </div>

                        {renderActions && (
                            <div className="d-flex align-items-center">{renderActions(shippingOrder)}</div>
                        )}
                    </div>

                    {(routeText || cargoText) && (
                        <div className="d-flex flex-wrap align-items-center text-muted small mt-2 pt-2 border-top">
                            {routeText && <span className="mr-3">Маршрут: {routeText}</span>}
                            {cargoText && <span>Груз: {cargoText}</span>}
                        </div>
                    )}
                </div>

                <div className="text-center pt-2">
                    <span
                        className="text-primary font-weight-bold cursor-pointer"
                        onClick={() => setIsCollapsed(false)}
                    >
                        Показать подробную информацию ЭЗЗ &darr;
                    </span>
                </div>
            </div>
        );
    };

    return (
        <>
            <div
                className="show-bid__section-header d-flex align-items-center justify-content-between cursor-pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <span>Электронная заказ-заявка</span>
                <i
                    className={cn("icon-uEA72-chevron-down b-etrn__chevron", {
                        "b-etrn__chevron--collapsed": isCollapsed,
                    })}
                />
            </div>
            {isCollapsed ? (
                renderSummaryCard()
            ) : (
                <ShippingOrderInfo
                    shippingOrder={shippingOrder}
                    renderActions={renderActions}
                    renderPrintFormAction={
                        canDownloadExternalPrintForms(roles)
                            ? () => (
                                  <ExternalPrintFormDownloadBtn
                                      documentType="shippingOrder"
                                      loading={isDownloading(shippingOrder.id)}
                                      onClick={() => download(shippingOrder.id)}
                                  />
                              )
                            : null
                    }
                />
            )}
        </>
    );
};

export default ShippingOrderSection;
